// src/app/services/auth.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Subject } from 'rxjs';
import { TokenModel } from '../models/token.model';
import { Router } from '@angular/router';
import { ResponseModel } from '../models/response.model';
import { FCMService } from '../services/fcm.service';
import { v4 as uuidv4 } from 'uuid';
import { LocalStorageRefService } from './local-storage-ref.service';
import { StorageKeysEnum } from '../enums/storage-keys.enum';
import { NgxPermissionsService } from 'ngx-permissions';
import { PermissionModel } from '../models/permission.model';
import { UserModel } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  loginUpdate = new Subject<string>();
  token$: BehaviorSubject<TokenModel | null> =
    new BehaviorSubject<TokenModel | null>(null);

  constructor(
    private http: HttpClient,
    private router: Router,
    private fcmService: FCMService,
    private permissionsService: NgxPermissionsService,
    private localStorageRefService: LocalStorageRefService,
  ) {
    this.token$.next(this.loadFromStorage());
  }

  public get currentUser() {
    return this.token$.value?.profile || null;
  }

  public get userCompanyId() {
    return this.token$.value?.profile.company.id || null;
  }

  login(username: string, password: string) {
    const data: {
      username: string;
      password: string;
      udid?: string;
      fcm_token?: string;
    } = {
      username: username,
      password: password,
    };
    const fcmToken = this.fcmService.getFcmToken();
    if (fcmToken) {
      const uuid = uuidv4();
      data['udid'] = uuid;
      data['fcm_token'] = fcmToken;
    }
    return this.http.post<ResponseModel<TokenModel>>('auth/login', data);
  }

  logout() {
    const user = this.token$.value;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${user?.access_token}`,
    });
    this.http
      .get<ResponseModel<TokenModel>>('auth/logout', {
        headers: headers,
      })
      .subscribe();
    this.unAuth().then();
  }

  async unAuth() {
    this.localStorageRefService.removeData(StorageKeysEnum.USER);
    this.localStorageRefService.removeData(StorageKeysEnum.FCM_TOKEN);
    this.token$.next(null);
    await this.router.navigate(['/auth/login']);
  }

  isLoggedIn() {
    return this.token$.value !== null;
  }

  handleAuthentication(token: TokenModel) {
    this.token$.next(token);
    this.localStorageRefService.setData(
      StorageKeysEnum.USER,
      JSON.stringify(token),
    );
  }

  private loadFromStorage(): TokenModel | null {
    const userData = this.localStorageRefService.getData(StorageKeysEnum.USER);
    if (typeof userData === 'string') {
      try {
        return JSON.parse(userData) as TokenModel;
      } catch (e) {
        console.error('Failed to parse user data from storage', e);
      }
    }
    return null;
  }

  addAuthHeader(
    request: HttpRequest<unknown>,
    user: TokenModel,
  ): HttpRequest<unknown> {
    if (user && user.access_token) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${user.access_token}`,
          Accept: 'application/json',
          'Access-Control-Allow-Origin': '*',
        },
      });
    }

    return request;
  }

  sendLoginUpdate(message: string) {
    this.loginUpdate.next(message);
  }

  getLoginUpdate() {
    return this.loginUpdate.asObservable();
  }

  loadPermissions(permissions: PermissionModel[]) {
    this.permissionsService.flushPermissions();
    this.permissionsService.loadPermissions(
      permissions.map((permission) => permission.name),
    );
  }

  loadUserFromStorage(): UserModel | null {
    const userJson =
      this.localStorageRefService.getData(StorageKeysEnum.USER) ?? null;
    if (userJson != null && userJson != 'undefined' && userJson != undefined) {
      const tokenModel = JSON.parse(userJson) as TokenModel;
      return tokenModel.profile;
    }
    return null;
  }
}
